import React, { useEffect, useState } from 'react';
import {
  Button, Box, Stack, ToggleButton, ToggleButtonGroup, TextField, Typography,
  Alert, Link, CircularProgress, Divider,
} from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { format } from 'date-fns';
import { availabilityService, AvailabilityChange } from '../../services/availabilityService';
import TimeSelect from './TimeSelect';

/**
 * The editor a drag opens, and that an existing block opens when clicked.
 *
 * The question it exists to ask is "this date, or every week?" - a drag on a
 * Tuesday could mean either, and writing one when the person meant the other
 * is the mistake worth preventing. So nothing is saved until that is
 * answered, and the times stay editable in between: a drag snaps to five
 * minutes, which is usually but not always what was meant.
 */

export type AvailabilityScope = 'date' | 'weekly';

export interface AvailabilitySave {
  start: string;
  end: string;
  scope: AvailabilityScope;
  validFrom: string | null;
  validTo: string | null;
  comment: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  date: Date | null;
  start: string;
  end: string;
  /** The two read-only modes look at hours this date will not give:
   *  'blocked' at an occurrence DELETED from it, to put back, and 'closed'
   *  at a span CLOSED on it, to lift. */
  mode: 'create' | 'edit' | 'blocked' | 'closed';
  /** Where the block came from; seeds the scope. */
  source?: 'weekly' | 'date' | 'cancelled' | 'blocked';
  validFrom?: string | null;
  validTo?: string | null;
  comment?: string | null;
  saving?: boolean;
  /** A refusal from the server, shown HERE rather than as a page toast: the
   *  editor is still open and is where the correction gets made. */
  error?: string | null;
  onSave: (payload: AvailabilitySave) => void;
  /** Remove it - from this date, or from the week, per the chosen scope. */
  onDelete?: () => void;
  /** Drop just this date's occurrence of a weekly block, leaving the rest. */
  onBlockThisDate?: () => void;
  /** Put a cancelled occurrence back. Only used in 'blocked' mode. */
  onRevive?: () => void;
  /** Close this slot: it stays in the diary, drawn in red, and no patient
   *  may be given it. The WHOLE slot - the pickers say what the hours are,
   *  not what to close, and the server refuses part of one - so this takes
   *  no times and the caller uses the slot's own. */
  onBlockHours?: (comment: string | null) => void;
  /** Lift a block, giving the hours back. Only used in 'closed' mode. */
  onUnblock?: () => void;
  /** Whose history, and which slot's. Absent for a block being created. */
  therapistUserId?: number;
  availabilityId?: number | null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                   'Friday', 'Saturday'];

const hhmm = (t: string) => (t || '').slice(0, 5);

/** Today, as the yyyy-MM-dd an <input type="date"> speaks. Local, via date-fns,
 *  not toISOString(): that is UTC and would answer yesterday after midnight
 *  UTC-plus zones - offering a date the server then clamps forward anyway. */
const todayISO = () => format(new Date(), 'yyyy-MM-dd');

const formatChangeTime = (iso: string | null): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/** What a logged change did, as a line someone can read. */
const describeChange = (c: AvailabilityChange): string => {
  if (!c.before) return `created: ${c.after || ''}`;
  if (!c.after) return `removed: ${c.before}`;
  return `${c.before} → ${c.after}`;
};

export const AvailabilityEditor: React.FC<Props> = ({
  open, onClose, date, start, end, mode, source = 'date',
  validFrom = null, validTo = null, comment = null, saving = false,
  error = null, onSave, onDelete, onBlockThisDate, onRevive,
  onBlockHours, onUnblock, therapistUserId, availabilityId,
}) => {
  const [from, setFrom] = useState(hhmm(start));
  const [to, setTo] = useState(hhmm(end));
  const [scope, setScope] = useState<AvailabilityScope>(
    source === 'weekly' ? 'weekly' : 'date');   // past dates are forced to weekly below
  const [vFrom, setVFrom] = useState(validFrom || '');
  const [vTo, setVTo] = useState(validTo || '');
  const [note, setNote] = useState(comment || '');
  // Loaded only when asked for: most edits do not need the history, and it
  // is a second round trip.
  const [history, setHistory] = useState<AvailabilityChange[] | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  /** A day that has already happened.
   *
   *  Hours that have passed cannot be changed - availability_service refuses
   *  it, because those minutes were offered to patients, booked against or
   *  refused, and rewriting them afterwards changes what the diary says it
   *  offered. The actions that would write to THIS DATE are therefore not
   *  offered at all: a control that always fails is worse than no control.
   *
   *  The weekly pattern stays editable from a past date. That write is not
   *  retroactive - the old hours are kept up to yesterday and the new ones
   *  start today - so it is a legitimate thing to do while looking back at a
   *  week, and the caption below says what it will mean. */
  const isPast = !!date && new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
  // A one-off on a past date can only be written to that date, so there is
  // nothing here to save or delete.
  const pastOneOff = isPast && source === 'date';

  // Re-seed when the editor is opened on a different block. Without this the
  // previous block's times linger and the next save writes them.
  useEffect(() => {
    if (!open) return;
    setFrom(hhmm(start));
    setTo(hhmm(end));
    // On a past date only the weekly pattern can be written, so the editor
    // opens on it whatever the block came from. isPast rather than the date
    // arithmetic repeated: one definition of "past", and a boolean this effect
    // can depend on without re-seeding every time the date object changes.
    setScope(source === 'weekly' || isPast ? 'weekly' : 'date');
    // A NEW weekly pattern starts TODAY. Hours before today were already
    // offered to patients, booked against or refused, so a pattern backdated
    // over them claims a past the diary did not have - and availability_service
    // pulls valid_from forward to today anyway (max(valid_from, today)), which
    // left the therapist looking at a date that was not what got saved.
    //
    // An EXISTING pattern keeps whatever it has, blank included: blank means
    // "no limit", and seeding today over it would quietly narrow a pattern
    // the person only opened to read.
    setVFrom(validFrom || (mode === 'create' ? todayISO() : ''));
    setVTo(validTo || '');
    setNote(comment || '');
    setHistory(null);
  }, [open, start, end, source, validFrom, validTo, comment, isPast, mode]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      setHistory(await availabilityService.history({
        userId: therapistUserId,
        availabilityId: availabilityId ?? undefined,
        // Without a slot id, narrow to the day being looked at - the
        // therapist's whole week in front of one block reads as noise.
        onDate: availabilityId ? undefined
          : (date ? format(date, 'yyyy-MM-dd') : undefined),
      }));
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const weekdayName = date ? DAY_NAMES[date.getDay()] : '';
  const invalid = !from || !to || from >= to;

  /** Looking at an existing refusal rather than at hours being written: no
   *  pickers, no Save. */
  const readOnly = mode === 'blocked' || mode === 'closed';

  const title = mode === 'create' ? 'Add Working Hours'
    : mode === 'blocked' ? 'Cancelled Hours'
    : mode === 'closed' ? 'Blocked Hours'
    : 'Edit Working Hours';

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xs"
      actions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                   width: '100%', px: 1 }}>

          {/* Putting a cancelled occurrence back writes to that date, so it
              is not offered once the date has passed. */}
          {mode === 'blocked' && onRevive && !isPast && (
            <Button onClick={onRevive} disabled={saving}
                    sx={CLINIC_DIALOG_SX.primaryButton}>
              Restore
            </Button>
          )}

          {/* Lifting a block writes to that date, so it is not offered once
              the date has passed. */}
          {mode === 'closed' && onUnblock && !isPast && (
            <Button onClick={onUnblock} disabled={saving}
                    sx={CLINIC_DIALOG_SX.primaryButton}>
              Unblock
            </Button>
          )}

          {!readOnly && !pastOneOff && (
            <Button
              disabled={saving || invalid}
              sx={{
                ...CLINIC_DIALOG_SX.primaryButton,
                backgroundColor: POPUP_COLORS.buttonText,
                color: '#fff',
                '&:hover': { backgroundColor: POPUP_COLORS.closeBtnHover },
              }}
              startIcon={saving ? <CircularProgress size={14} /> : undefined}
              onClick={() => onSave({
                start: from, end: to, scope,
                validFrom: scope === 'weekly' && vFrom ? vFrom : null,
                validTo: scope === 'weekly' && vTo ? vTo : null,
                comment: note.trim() || null,
              })}
            >
              Save
            </Button>
          )}

          <Button onClick={onClose} disabled={saving}
                  sx={CLINIC_DIALOG_SX.cancelButton}>
            Cancel
          </Button>

          {/* Pushed away from the others on purpose: Delete removes the
              whole pattern, and it should not sit a few pixels from Save. */}
          <Box sx={{ flex: 1 }} />
          {mode === 'edit' && onDelete && !pastOneOff && (
            <Button
              onClick={onDelete} disabled={saving}
              sx={{ ...CLINIC_DIALOG_SX.cancelButton,
                    backgroundColor: '#fdeaea', color: '#9b2c2c',
                    '&:hover': { backgroundColor: '#f8d7d7' } }}
            >
              Delete
            </Button>
          )}
        </Box>
      )}
    >
      <Stack spacing={2}>
        {date && (
          <Typography variant="body2" align="center"
                      sx={{ color: POPUP_COLORS.titleText }}>
            {format(date, 'EEEE, d MMMM yyyy')}
          </Typography>
        )}
          {/* The server's refusal belongs here, next to the field that has
              to change, not in a toast that disappears. */}
          {error && <Alert severity="error">{error}</Alert>}

          {mode === 'closed' ? (
            <Alert severity="warning">
              {hhmm(start)}–{hhmm(end)} is blocked on this date — the hours are
              still in the diary, but nobody can be offered them. Unblocking
              lifts that and leaves the hours where they are.
            </Alert>
          ) : mode === 'blocked' ? (
            <Alert severity="warning">
              {hhmm(start)}–{hhmm(end)} was cancelled for this date — the hours
              are not there at all. Restoring brings back this occurrence only;
              the weekly pattern is unchanged.
            </Alert>
          ) : (
            <>
              {/* Hour and minute separately. One list of 288 five-minute
                  options meant scrolling past a hundred and seventy entries
                  to reach 14:35; and it was not obvious which select was the
                  start and which the end, so both now carry their own
                  label. */}
              <TimeSelect label="From" value={from} onChange={setFrom} />
              <TimeSelect label="To" value={to} onChange={setTo} />
              {invalid && (
                <Typography variant="caption" color="error">
                  The start must be before the end.
                </Typography>
              )}

              {/* The question this editor exists to ask. */}
              {/* The chosen scope is FILLED, not merely shaded. MUI's
                  default selected state is a faint grey that reads as
                  disabled on this teal paper - and the one thing this popup
                  exists to make unambiguous is which of the two was picked. */}
              <ToggleButtonGroup
                size="small" exclusive value={scope}
                onChange={(_, v) => v && setScope(v)}
                sx={{
                  // Two separate pills, not a joined segmented control: each
                  // is a whole choice, and butted together at half the
                  // dialog's width they read as one wide control whose left
                  // half happens to be dark.
                  gap: 1,
                  justifyContent: 'center',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                    borderRadius: '8px !important',
                    marginLeft: 0,
                  },
                  '& .MuiToggleButton-root': {
                    backgroundColor: POPUP_COLORS.fieldBackground,
                    color: POPUP_COLORS.buttonText,
                    textTransform: 'none',
                    fontWeight: 500,
                    py: 0.6,
                    px: 2,
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: POPUP_COLORS.buttonHover },
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    backgroundColor: POPUP_COLORS.buttonText,
                    color: '#fff',
                    '&:hover': { backgroundColor: POPUP_COLORS.closeBtnHover },
                  },
                }}
              >
                {!isPast && <ToggleButton value="date">This date only</ToggleButton>}
                <ToggleButton value="weekly">Every {weekdayName || 'week'}</ToggleButton>
              </ToggleButtonGroup>

              {isPast && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1,
                                                    color: POPUP_COLORS.buttonText }}>
                  {pastOneOff
                    ? 'This date has passed - hours that have already been offered cannot be changed.'
                    : `This date has passed. A change here applies to every ${weekdayName || 'week'} from today; the hours already offered stay as they were.`}
                </Typography>
              )}

              {scope === 'weekly' && (
                <>
                  {/* One caption for the pair, instead of helper text under
                      each: "empty = always" and "empty = no end" said the
                      same thing twice and pushed the fields apart. */}
                  <Typography variant="caption" align="right"
                              sx={{ color: POPUP_COLORS.titleText, mt: -1 }}>
                    Period — empty = no limit
                  </Typography>
                  <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                    <Typography sx={CLINIC_DIALOG_SX.fieldLabel}>From date</Typography>
                    {/* No earlier than today - the picker will not go back past
                        the point the server would clamp to anyway. An existing
                        pattern that already starts in the past keeps its own
                        date as the floor, so reading or editing one never
                        invalidates it; it just cannot be pushed further back. */}
                    <TextField
                      type="date" size="small" fullWidth
                      sx={CLINIC_DIALOG_SX.select}
                      inputProps={{
                        min: vFrom && vFrom < todayISO() ? vFrom : todayISO(),
                      }}
                      value={vFrom} onChange={(e) => setVFrom(e.target.value)}
                    />
                  </Box>
                  <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                    <Typography sx={CLINIC_DIALOG_SX.fieldLabel}>To date</Typography>
                    <TextField
                      type="date" size="small" fullWidth
                      sx={CLINIC_DIALOG_SX.select}
                      inputProps={{ min: vFrom || todayISO() }}
                      value={vTo} onChange={(e) => setVTo(e.target.value)}
                    />
                  </Box>
                </>
              )}

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography sx={CLINIC_DIALOG_SX.fieldLabel}>Comment</Typography>
                <TextField
                  size="small" fullWidth value={note}
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="why these hours are what they are"
                />
              </Box>
            </>
          )}

          {/* Dropping ONE occurrence of a weekly pattern. In the body and
              full width, not in the footer: it is a different action from
              Delete, and sitting beside it as a cramped two-line button was
              exactly the confusion to avoid. */}
          {mode === 'edit' && onBlockThisDate && source === 'weekly' && !isPast && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={onBlockThisDate} disabled={saving}
                sx={{ ...CLINIC_DIALOG_SX.primaryButton, py: 0.6 }}
              >
                Cancel this date only
              </Button>
            </Box>
          )}

          {/* Blocking, as distinct from deleting. It closes this whole slot:
              the hours stay in the diary, drawn in red, and turning the
              block off makes it an active slot again. Only ever this date -
              a standing refusal is hours the therapist does not work, which
              is the weekly pattern's job. */}
          {!readOnly && onBlockHours && !isPast && (
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={() => onBlockHours(note.trim() || null)}
                disabled={saving || invalid}
                sx={{ ...CLINIC_DIALOG_SX.cancelButton, py: 0.6,
                      backgroundColor: '#fdeaea', color: '#9b2c2c',
                      '&:hover': { backgroundColor: '#f8d7d7' } }}
              >
                Block this date only
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5,
                                                  color: POPUP_COLORS.buttonText }}>
                {hhmm(start)}–{hhmm(end)} stays in the diary, offered to nobody.
              </Typography>
            </Box>
          )}

          {/* Behind a link: it is a second request, and most edits do not
              need it. */}
          {mode !== 'create' && (
            <Box>
              {history === null ? (
                <Link component="button" type="button" underline="hover"
                      onClick={loadHistory} disabled={loadingHistory}>
                  {loadingHistory ? 'Loading history…' : 'Show change history'}
                </Link>
              ) : (
                <>
                  <Divider sx={{ mb: 1 }} />
                  {history.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No recorded changes for this slot.
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {history.map((c, i) => (
                        <Typography key={i} variant="caption" color="text.secondary">
                          {formatChangeTime(c.at)} · {c.by} · {describeChange(c)}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Box>
          )}
      </Stack>
    </ClinicDialog>
  );
};

export default AvailabilityEditor;
